import { AsyncPipe, NgIf } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import { FlexModule } from '@angular/flex-layout';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import {
  MatPaginator,
  MatPaginatorModule,
  PageEvent
} from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { DateFormatPipe, MomentModule } from 'ngx-moment';
import {
  combineLatest,
  map,
  mergeMap,
  of,
  Subject,
  take,
  takeUntil
} from 'rxjs';
import { PreviewDocketDialogComponent } from 'src/app/shared/dialogs/preview-docket-dialog/preview-docket-dialog.component';
import { HlCurrencyPipe } from 'src/app/shared/pipes/hl-currency.pipe';
import { Docket } from '../../models/index';
import { DocketService } from '../../shared/index';
import { CustomerNamePipe } from '../../shared/pipes/customer-name.pipe';
import { ConfigService } from '../../shared/services/config.service';
import { BatchRolloverDialogComponent } from '../shared/batch-rollover-dialog/batch-rollover-dialog.component';
import { CompleteBatchRolloverDialogComponent } from '../shared/complete-batch-rollover-dialog/complete-batch-rollover-dialog.component';
import { ReportCriteriaItemModel } from '../shared/models/report-criteria-item.model';
import { ReportCriteriaComponent } from '../shared/report-criteria/report-criteria.component';
import { BatchInvoiceReportStoreService } from './batch-invoice-report-store.service';

@Component({
  selector: 'app-report',
  templateUrl: './batch-invoice-report.component.html',
  styleUrls: ['./batch-invoice-report.component.css'],
  providers: [
    DocketService,
    DateFormatPipe,
    CustomerNamePipe,
    BatchInvoiceReportStoreService
  ],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FlexModule,
    ReportCriteriaComponent,
    NgIf,
    MatButtonModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatIconModule,
    RouterModule,
    AsyncPipe,
    ExtendedModule,
    MomentModule,
    HlCurrencyPipe
  ]
})
export class BatchInvoiceReportComponent implements AfterViewInit, OnDestroy {
  @ViewChild('formContainer', { static: true }) formContainer;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  displayedColumns: string[] = [
    'select',
    'full_name',
    'contract',
    'created',
    'closed',
    'hires',
    'consumables',
    'deliveries',
    'preview'
  ];

  // Store selectors
  dockets$ = this.store.dockets$;
  loading$ = this.store.loadingResults$;
  pagination$ = this.store.pagination$;
  total$ = this.store.totalItems$;
  pageSize$ = this.store.pageSize$;
  pageIndex$ = this.store.pageIndex$;
  summaryData$ = this.store.summaryData$;
  showRolloverButton$ = this.store.showRolloverButton$;

  showBulkUpdateButton$ = this.store.anySelected$;
  selectedDocketIds$ = this.store.selectedDocketIds$;
  allSelected$ = this.store.allSelected$;
  indeterminateSelected$ = combineLatest([
    this.allSelected$,
    this.selectedDocketIds$
  ]).pipe(
    map(
      ([allSelected, selectedDocketIds]) =>
        !allSelected && selectedDocketIds.length > 0
    )
  );
  toggleAllSelected = this.store.toggleAllSelected;
  toggleSelected = this.store.toggleSelected;
  selectedIdMap$ = this.store.selectedDocketIds$.pipe(
    map((ids) => ids.reduce((acc, id) => ({ ...acc, [id]: true }), {}))
  );

  dateFormat = ConfigService.dates.shortDateTimeFormat;

  availableCriteria: ReportCriteriaItemModel[] = [
    {
      name: 'Created Date',
      type: 'fromToDate',
      reference: 'created',
      used: false
    }
  ];

  orderByCols = [
    { name: 'Customer', value: 'full_name' },
    { name: 'Created Date', value: 'created' },
    { name: 'Invoice Date', value: 'closed' },
    { name: 'User', value: 'user_id' },
    { name: 'Invoice No.', value: 'account_reference' },
    { name: 'Modified Date', value: 'modified' }
  ];

  selectedStatus: string;

  selectedRows: Docket[] = [];

  orderBy = {
    order: 'id',
    direction: 'asc'
  };

  reportForm = new UntypedFormGroup({});

  reportFetched = false;

  private destroy$ = new Subject<void>();

  constructor(
    private dialog: MatDialog,
    private store: BatchInvoiceReportStoreService
  ) {}

  ngAfterViewInit() {
    // Set up sorting and pagination change detection
    this.sort.sortChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((sort: Sort) => {
        this.store.setSort({ sortBy: sort.active, sortOrder: sort.direction });
      });

    this.paginator.page
      .pipe(takeUntil(this.destroy$))
      .subscribe((page: PageEvent) => {
        this.store.setPage({ page: page.pageIndex, perPage: page.pageSize });
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getDockets() {
    this.reportFetched = true;

    // Prepare filters from form
    const filters: any = {};

    this.availableCriteria.forEach((available) => {
      if (available.used) {
        if (available.reference === 'created') {
          filters.created = {
            from: available.formControl.value.from,
            to: available.formControl.value.to
          };
        } else if (available.reference === 'closed') {
          filters.closed = {
            from: available.formControl.value.from,
            to: available.formControl.value.to
          };
        }
      }
    });

    // Set filters in store
    this.store.setFilters(filters);

    // Generate report
    this.store.generateReport();

    this.reportForm.markAsPristine();
  }

  showBatchRolloverDialog() {
    this.selectedDocketIds$
      .pipe(
        takeUntil(this.destroy$),
        take(1),
        mergeMap((selectedIds) => {
          const dialogSettings = {
            data: { docketIds: selectedIds },
            width: '800px',
            maxHeight: '80vh'
          };
          return this.dialog
            .open(BatchRolloverDialogComponent, dialogSettings)
            .afterClosed();
        }),
        mergeMap((docketsToRollover) => {
          if (docketsToRollover) {
            // Open the complete batch rollover dialog
            return this.dialog
              .open(CompleteBatchRolloverDialogComponent, {
                width: '500px',
                disableClose: true,
                data: {
                  docketIds: docketsToRollover
                }
              })
              .afterClosed()
              .pipe(map(() => true));
          }
          return of(false);
        })
      )
      .subscribe((docketsToRollover) => {
        if (docketsToRollover) {
          this.store.generateReport();
        }
      });
  }

  previewDocket(docketId: number) {
    const dialogData = {
      docketId: docketId
    };

    const dialogRef = this.dialog.open(PreviewDocketDialogComponent, {
      data: dialogData
    });

    dialogRef.afterClosed().subscribe((updateClicked) => {});
  }
}
